/**
 * Le chiffrage d'un local — le tableau du classeur, rempli par ce que
 * l'affaire sait, puis par une mission.
 *
 * Trois sources, dans l'ordre de confiance affiché ligne à ligne : le
 * relevé (S, P — géométrie vérifiée), les décisions du métreur (elles
 * priment), et les traces de calcul de la mission — chacune avec son
 * contrôle (vérifiée, invérifiable, contredite). Ce qu'aucune source ne
 * porte reste « — » : un tableau qui comble ses vides fabrique un devis
 * faux.
 *
 * **La mission est une vraie mission** : son fil apparaît dans l'Assistant,
 * ses arbitrages s'y répondent — un seul guichet. L'écran suit le journal
 * par le flux SSE existant, et l'état est une machine : lecture, prêt,
 * lancement, mission (en course ou suspendue), dépouillement, échec.
 *
 * **L'écran se rattache, il ne se souvient pas.** Changer d'onglet démonte
 * tout ; au remontage, l'overlay demande au serveur s'il existe une mission
 * de chiffrage pour ce local — et la suit : le flux rejoue le journal depuis
 * le début, la narration et le guichet se reconstruisent d'eux-mêmes. Et le
 * guichet vit **ici** : les arbitrages se répondent dans l'overlay, sans
 * détour par l'Assistant — qui reste ouvert à qui le préfère.
 */
import { useEffect, useRef, useState } from "react";

import { api, ErreurApi } from "../../api/client";
import { useModale } from "../../composants/clavier";
import { MOT_MISSION } from "../../composants/etiquettes";
import { Guichet, Visionneuse, type ImageOuverte } from "./Chat";

interface Ligne {
  clef: string;
  libelle: string;
  unite: string;
  groupe: string;
  valeur: number | null;
  origine: string | null;
  controle: string | null;
  operation: string | null;
  prescrite: { lot: string; article: string; page: number | null } | null;
}

interface EvenementDeMission {
  mission_id?: string;
  seq: number;
  genre: string;
  charge: Record<string, unknown>;
  quand: string;
}

interface Tableau {
  local: string;
  lignes: Ligne[];
  notes?: string[];
  autres_calculs: {
    operation: string; valeur: number; unite: string; controle: string;
  }[];
  chiffrees: number;
  sur: number;
}

type Etat =
  | { nom: "lecture" }
  | { nom: "pret" }
  | { nom: "lancement" }
  | { nom: "mission"; mission: string; conversation: string;
      allure: "en file" | "en course" | "suspendue" }
  | { nom: "depouillement" }
  | { nom: "echec"; motif: string };

const CONTROLES: Record<string, string> = {
  "vérifiée": "chiffrage__controle--ok",
  contredite: "chiffrage__controle--ko",
  invérifiable: "chiffrage__controle--doute",
};

export function Chiffrage({
  affaireId, local, aire, perimetre, releve, fermer,
}: {
  affaireId: string;
  local: string;
  aire: number | null;
  perimetre: number | null;
  releve?: string;
  fermer: () => void;
}) {
  const [etat, poserEtat] = useState<Etat>({ nom: "lecture" });
  const [tableau, poserTableau] = useState<Tableau | null>(null);
  const [bord, poserBord] = useState<string[]>([]);
  const [attente, poserAttente] = useState<EvenementDeMission | null>(null);
  const [confirmerArret, poserConfirmerArret] = useState(false);
  const [loupe, poserLoupe] = useState<ImageOuverte | null>(null);
  // Une confirmation explicative, comme dans l'Assistant — plutôt qu'un bouton
  // à deux clics : le même geste destructeur se comporte pareil partout.
  const refArret = useModale(confirmerArret, () => poserConfirmerArret(false));
  const bordRef = useRef<HTMLDivElement>(null);
  const sseRef = useRef<EventSource | null>(null);
  const seqVus = useRef<Set<number>>(new Set());
  // Le rang de la dernière question déjà répondue : son rejeu ne rouvre pas
  // le guichet — répondre est un fait, pas un état d'écran.
  const seqRepondu = useRef(0);

  const noter = (ligne: string) =>
    poserBord((avant) => [...avant.slice(-60), ligne]);

  async function relire(): Promise<Tableau | null> {
    const parametres = new URLSearchParams({ local });
    if (aire != null) parametres.set("aire", String(aire));
    if (perimetre != null) parametres.set("perimetre", String(perimetre));
    if (releve) parametres.set("releve", releve);
    try {
      const corps = await api.lire<{ donnees: Tableau }>(
        `/api/v1/affaires/${affaireId}/chiffrages/tableau?${parametres}`);
      poserTableau(corps.donnees);
      return corps.donnees;
    } catch (exc) {
      poserEtat({ nom: "echec",
                  motif: exc instanceof ErreurApi ? exc.message
                    : "Le tableau ne se lit pas." });
      return null;
    }
  }

  useEffect(() => {
    void (async () => {
      const lu = await relire();
      if (!lu) return;
      noter(`Tableau ouvert : ${lu.chiffrees} ligne(s) déjà connues sur `
        + `${lu.sur} — relevé et annotations du métreur.`);
      // Le rattachement : une mission de ce local existe-t-elle déjà ?
      try {
        const corps = await api.lire<{
          donnees: { mission_id: string; conversation_id: string | null;
                     etat: string } | null;
        }>(`/api/v1/affaires/${affaireId}/chiffrages/en-cours`
          + `?local=${encodeURIComponent(local)}`);
        const trouvee = corps.donnees;
        // `QUEUED` se rattache comme les autres, et l'oublier était le pire
        // des trois : l'écran retombait sur « prêt » et proposait de lancer
        // une mission de chiffrage **déjà inscrite**. Le métreur en aurait
        // ouvert deux sur le même local, chacune écrivant ses lignes.
        const ALLURES: Record<string, "en file" | "en course" | "suspendue"> = {
          QUEUED: "en file", RUNNING: "en course", SUSPENDED: "suspendue",
        };
        const allure = trouvee ? ALLURES[trouvee.etat] : undefined;
        if (trouvee && allure) {
          noter(`Mission retrouvée (${allure}) — le journal se rejoue.`);
          poserEtat({ nom: "mission", mission: trouvee.mission_id,
                      conversation: trouvee.conversation_id ?? "", allure });
          suivre(trouvee.mission_id, trouvee.conversation_id ?? "");
          return;
        }
        // Une mission finie mérite d'être dite : son dépouillement est déjà
        // dans le tableau — et ce qui n'a pas retombé sur une ligne attend
        // en bas, pas dans le néant.
        if (trouvee && trouvee.etat === "DONE") {
          noter("Mission de chiffrage terminée retrouvée — son dépouillement"
            + " est dans le tableau."
            + (lu.autres_calculs.length
              ? ` ${lu.autres_calculs.length} calcul(s) n'ont retombé sur`
                + " aucune ligne du catalogue : ils sont en bas du tableau,"
                + " à relire."
              : ""));
        }
        if (trouvee && trouvee.etat === "FAILED") {
          noter("⚠ La dernière mission de chiffrage a échoué — le fil est"
            + " dans l'Assistant ; relancez quand vous voudrez.");
        }
      } catch {
        // Un rattachement qui échoue n'empêche pas de lire le tableau.
      }
      poserEtat({ nom: "pret" });
    })();
    return () => sseRef.current?.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affaireId, local]);

  // Échap ferme le chiffrage seul — l'atelier, dessous, reste ouvert.
  useEffect(() => {
    const surTouche = (touche: KeyboardEvent) => {
      if (touche.key === "Escape") {
        touche.stopPropagation();
        fermer();
      }
    };
    window.addEventListener("keydown", surTouche, true);
    return () => window.removeEventListener("keydown", surTouche, true);
  }, [fermer]);

  useEffect(() => {
    bordRef.current?.scrollTo({ top: bordRef.current.scrollHeight });
  }, [bord.length]);

  async function lancer() {
    poserEtat({ nom: "lancement" });
    noter("Mission demandée — relevé, prescription, calcul.");
    try {
      const corps = await api.ecrire<{
        donnees: { mission_id: string; conversation_id: string };
      }>(`/api/v1/affaires/${affaireId}/chiffrages`, {
        local, aire_m2: aire, perimetre_m: perimetre,
      });
      const { mission_id, conversation_id } = corps.donnees;
      poserEtat({ nom: "mission", mission: mission_id,
                  conversation: conversation_id, allure: "en course" });
      suivre(mission_id, conversation_id);
    } catch (exc) {
      poserEtat({ nom: "echec",
                  motif: exc instanceof ErreurApi ? exc.message
                    : "La mission n'est pas partie." });
    }
  }

  /** Les extraits d'un point d'arbitrage : seuls les chemins servis par
   *  l'API se rendent — un chemin absolu du serveur resterait une image
   *  cassée, autant taire l'image et garder la question. */
  function servable(evenement: EvenementDeMission): EvenementDeMission {
    const charge = { ...evenement.charge };
    const nettoyer = (point: Record<string, unknown>) => {
      const image = String(point.image ?? "");
      return image && !image.startsWith("/api/")
        ? { ...point, image: "" } : point;
    };
    if (Array.isArray(charge.items)) {
      charge.items = (charge.items as Record<string, unknown>[]).map(nettoyer);
    }
    Object.assign(charge, nettoyer(charge));
    return { ...evenement, charge };
  }

  function suivre(mission: string, conversation: string) {
    sseRef.current?.close();
    const source = new EventSource(
      `/api/v1/affaires/${affaireId}/missions/${mission}/flux?depuis=0`);
    sseRef.current = source;
    source.onmessage = (message) => {
      const evenement = JSON.parse(message.data) as EvenementDeMission;
      const deja = seqVus.current.has(evenement.seq);
      seqVus.current.add(evenement.seq);
      if (evenement.genre === "pas" && evenement.charge?.etat === "debut"
          && !deja) {
        const nom = String(evenement.charge.nom ?? "");
        if (nom) noter(`… ${nom}`);
      }
      if (evenement.genre === "question"
          && evenement.seq > seqRepondu.current) {
        poserAttente(servable(evenement));
        poserEtat({ nom: "mission", mission, conversation,
                    allure: "suspendue" });
        if (!deja) {
          noter("⚠ La mission attend vos arbitrages — le guichet est"
            + " ci-contre, le tableau reprendra ensuite.");
        }
      }
      if (evenement.genre === "reponse"
          && evenement.charge?.validation === undefined) {
        poserAttente(null);
        poserEtat({ nom: "mission", mission, conversation,
                    allure: "en course" });
      }
      if (evenement.genre === "echec") {
        poserEtat({ nom: "echec",
                    motif: String(evenement.charge?.motif ?? "mission en échec") });
      }
    };
    source.addEventListener("fin", () => {
      source.close();
      sseRef.current = null;
      void (async () => {
        poserEtat((courant) =>
          courant.nom === "mission" && courant.allure === "suspendue"
            ? courant : { nom: "depouillement" });
        const relu = await relire();
        if (relu) {
          poserEtat((courant) => {
            if (courant.nom === "mission" && courant.allure === "suspendue") {
              return courant;
            }
            noter(`Dépouillement : ${relu.chiffrees}/${relu.sur} lignes`
              + " chiffrées.");
            return { nom: "pret" };
          });
        }
      })();
    });
    source.onerror = () => {
      // Le flux se rouvre seul (EventSource) ; on ne crie pas à la coupure.
    };
  }

  async function arreter(mission: string) {
    try {
      await api.ecrire(
        `/api/v1/affaires/${affaireId}/missions/${mission}/arret`);
      noter("Mission arrêtée — le journal garde les pas faits, vos décisions"
        + " restent acquises.");
    } catch (exc) {
      noter(`⚠ ${exc instanceof ErreurApi ? exc.message
        : "L'arrêt n'a pas abouti."}`);
    } finally {
      poserConfirmerArret(false);
    }
  }

  async function repondreAuGuichet(mission: string, conversation: string,
                                   reponses: string[],
                                   motifs?: string[]): Promise<boolean> {
    try {
      await api.ecrire(
        `/api/v1/affaires/${affaireId}/missions/${mission}/arbitrages`,
        { reponses, ...(motifs ? { motifs } : {}) });
      seqRepondu.current = Math.max(seqRepondu.current, attente?.seq ?? 0);
      poserAttente(null);
      poserEtat({ nom: "mission", mission, conversation,
                  allure: "en course" });
      noter("Décisions transmises — la mission reprend.");
      suivre(mission, conversation);
      return true;
    } catch (exc) {
      noter(`⚠ ${exc instanceof ErreurApi ? exc.message
        : "Les décisions ne sont pas parties."}`);
      return false;
    }
  }

  const groupes = tableau
    ? [...new Set(tableau.lignes.map((ligne) => ligne.groupe))]
    : [];
  const enMission = etat.nom === "lancement" || etat.nom === "mission"
    || etat.nom === "depouillement";

  return (
    <div className="chiffrage" role="dialog" aria-modal="true"
         aria-label={`Chiffrage de ${local}`}>
      <div className="chiffrage__cadre">
        <div className="chiffrage__tete">
          <strong className="tronque">Chiffrage — {local}</strong>
          <span className={`etiquette chiffrage__etat chiffrage__etat--${etat.nom}`}>
            {etat.nom === "lecture" && "lecture…"}
            {etat.nom === "pret" && (tableau
              ? `${tableau.chiffrees}/${tableau.sur} chiffrées${
                  tableau.autres_calculs.length
                    ? ` · ${tableau.autres_calculs.length} à relire` : ""}`
              : "prêt")}
            {etat.nom === "lancement" && "lancement…"}
            {etat.nom === "mission" && `mission ${
              { "en file": MOT_MISSION.QUEUED,
                "en course": MOT_MISSION.RUNNING,
                "suspendue": MOT_MISSION.SUSPENDED }[etat.allure]}`}
            {etat.nom === "depouillement" && "dépouillement…"}
            {etat.nom === "echec" && "échec"}
          </span>
          {(etat.nom === "mission" || etat.nom === "depouillement") && (
            <span className="chat__pouls" aria-hidden="true" />
          )}
          {etat.nom === "pret" && (
            <button type="button" className="bouton bouton--primaire"
                    onClick={() => void lancer()}>
              {tableau && tableau.chiffrees > 2
                ? "Relancer la mission de chiffrage"
                : "Lancer la mission de chiffrage"}
            </button>
          )}
          {etat.nom === "mission" && etat.allure === "suspendue" && (
            <a className="bouton"
               href={`/affaires/${affaireId}?onglet=assistant`}>
              Répondre dans l'Assistant
            </a>
          )}
          {etat.nom === "mission" && (
            <button type="button" className="bouton"
                    onClick={() => poserConfirmerArret(true)}>
              Arrêter
            </button>
          )}
          <button type="button" className="chat__voile-fermer chiffrage__fermer"
                  aria-label="Fermer le chiffrage" title="Fermer (Échap)"
                  onClick={fermer}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        {etat.nom === "echec" ? (
          <div className="chiffrage__echec">
            <strong>Le chiffrage n'a pas abouti.</strong>
            <p>{etat.motif}</p>
          </div>
        ) : (
          <div className="chiffrage__corps">
            <div className="defilant chiffrage__table">
              {attente && etat.nom === "mission" && (
                <div className="chiffrage__guichet">
                  <Guichet
                    evenement={attente}
                    actif={etat.allure === "suspendue"}
                    repondre={(reponses, motifs) => repondreAuGuichet(
                      etat.mission, etat.conversation, reponses, motifs)}
                    agrandir={poserLoupe}
                  />
                </div>
              )}
              <table className="tableau">
                <thead>
                  <tr>
                    <th>Ouvrage</th>
                    <th className="chiffrage__nombre">Quantité</th>
                    <th>Unité</th>
                    <th>Origine</th>
                    <th>Contrôle</th>
                  </tr>
                </thead>
                <tbody>
                  {groupes.map((groupe) => (
                    <GroupeDeLignes key={groupe} groupe={groupe}
                                    lignes={(tableau?.lignes ?? [])
                                      .filter((l) => l.groupe === groupe)} />
                  ))}
                </tbody>
              </table>
              {tableau && (tableau.notes?.length ?? 0) > 0 && (
                <div className="chiffrage__autres">
                  {tableau.notes!.map((note) => (
                    <p key={note}>⚠ {note}</p>
                  ))}
                </div>
              )}
              {tableau && tableau.autres_calculs.length > 0 && (
                <div className="chiffrage__autres">
                  <strong>Autres calculs de la mission</strong> — hors
                  catalogue, à relire :
                  {tableau.autres_calculs.map((autre) => (
                    <p key={autre.operation} className="mono">
                      {autre.operation} = {autre.valeur} {autre.unite}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <div className="chiffrage__bord" ref={bordRef}
                 aria-live="polite">
              {bord.map((ligne, rang) => (
                <div key={rang}>{ligne}</div>
              ))}
              {enMission && etat.nom !== "depouillement" && (
                <div className="discret">
                  La mission tourne côté serveur : cette fenêtre peut se
                  fermer, le fil reste dans l'Assistant.
                </div>
              )}
            </div>
          </div>
        )}
        {loupe && <Visionneuse image={loupe}
                                fermer={() => poserLoupe(null)} />}
      </div>

      {confirmerArret && etat.nom === "mission" && (
        <div ref={refArret} className="voile" role="dialog" aria-modal="true"
             aria-label="Arrêter la mission">
          <div className="modale" style={{ width: "min(480px, 100%)" }}>
            <div className="modale__tete"><h2>Arrêter la mission ?</h2></div>
            <div className="modale__corps"
                 style={{ padding: "var(--mo-space-5)" }}>
              <div className="avis avis--attention">
                Elle s'arrêtera où elle en est. Le journal garde les pas faits,
                et vos décisions restent acquises au registre — une prochaine
                mission ne les redemandera pas.
              </div>
            </div>
            <div className="modale__pied">
              <span style={{ flex: 1 }} />
              <button type="button" className="bouton"
                      onClick={() => poserConfirmerArret(false)}>
                Annuler
              </button>
              <button type="button" className="bouton bouton--danger"
                      onClick={() => void arreter(etat.mission)}>
                Arrêter maintenant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GroupeDeLignes({ groupe, lignes }: { groupe: string; lignes: Ligne[] }) {
  return (
    <>
      <tr className="chiffrage__groupe">
        <td colSpan={5}>{groupe}</td>
      </tr>
      {lignes.map((ligne) => (
        <tr key={ligne.clef}
            className={ligne.valeur == null ? "chiffrage__vide" : ""}>
          <td title={ligne.operation ?? undefined}>{ligne.libelle}</td>
          <td className="chiffrage__nombre">
            {ligne.valeur == null ? "—"
              : ligne.valeur.toLocaleString("fr-FR",
                  { maximumFractionDigits: 2 })}
          </td>
          <td className="discret">{ligne.unite}</td>
          <td className="discret">
            {ligne.origine
              ?? (ligne.prescrite
                ? `prescrit — ${ligne.prescrite.lot} art. ${ligne.prescrite.article}`
                  + (ligne.prescrite.page ? ` p.${ligne.prescrite.page}` : "")
                : "")}
          </td>
          <td>
            {ligne.controle && (
              <span className={`etiquette ${CONTROLES[ligne.controle] ?? ""}`}>
                {ligne.controle}
              </span>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}
